import React, { useState, useMemo, useRef, useEffect } from "react";
import {
	ClipboardList,
	Search,
	ChevronDown,
	ChevronUp,
	Clock,
	Hash,
	Trash2,
	TrendingUp,
	Calendar,
} from "lucide-react";
import CustomModal from "./CustomModal";

const UserWorkouts = ({
	sharedWorkouts,
	workoutScores,
	addWorkoutScore,
	deleteWorkoutScore,
	loading,
}) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedDate, setSelectedDate] = useState("");
	const [selectedWorkout, setSelectedWorkout] = useState(null);
	const [selectedCategory, setSelectedCategory] = useState("");
	const [scoreType, setScoreType] = useState("time"); // "time" or "reps"
	const [score, setScore] = useState("");
	const [comment, setComment] = useState("");
	const [completedDate, setCompletedDate] = useState(
		new Date().toISOString().split("T")[0],
	);
	const [showHistory, setShowHistory] = useState({});
	const scoreFormRef = useRef(null);
	const [modal, setModal] = useState({
		show: false,
		title: "",
		message: "",
		type: "info",
	});
	const [confirmModal, setConfirmModal] = useState({
		show: false,
		title: "",
		message: "",
		type: "delete",
		onConfirm: null,
	});

	// When a workout is selected, scroll the score form into view so mobile users
	// (where the form renders at the top of the list) aren't left wondering where
	// the form went.
	useEffect(() => {
		if (selectedWorkout && scoreFormRef.current) {
			scoreFormRef.current.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}
	}, [selectedWorkout]);

	// Get unique workout dates sorted newest first
	const workoutDates = useMemo(() => {
		const dates = new Map();
		sharedWorkouts.forEach((w) => {
			const dateStr = new Date(w.createdAt).toLocaleDateString("en-GB", {
				weekday: "short",
				day: "numeric",
				month: "short",
				year: "numeric",
			});
			const dateKey = new Date(w.createdAt).toISOString().split("T")[0];
			if (!dates.has(dateKey)) {
				dates.set(dateKey, dateStr);
			}
		});
		return Array.from(dates.entries()).sort((a, b) => b[0].localeCompare(a[0]));
	}, [sharedWorkouts]);

	const filteredWorkouts = useMemo(() => {
		let results = sharedWorkouts;
		if (searchTerm.trim()) {
			const term = searchTerm.toLowerCase();
			results = results.filter((w) => w.title.toLowerCase().includes(term));
		}
		if (selectedDate) {
			results = results.filter((w) => {
				const wDate = new Date(w.createdAt).toISOString().split("T")[0];
				return wDate === selectedDate;
			});
		}
		return results;
	}, [sharedWorkouts, searchTerm, selectedDate]);

	const getCategories = (workout) => {
		const cats = [];
		if (workout.foundation)
			cats.push({
				key: "foundation",
				label: "Foundation",
				color: "bg-success",
			});
		if (workout.build)
			cats.push({
				key: "build",
				label: "Build",
				color: "bg-warning text-dark",
			});
		if (workout.peak)
			cats.push({ key: "peak", label: "Peak", color: "bg-danger" });
		return cats;
	};

	const getScoresForWorkout = (workoutId) => {
		return workoutScores
			.filter((s) => s.workoutId === workoutId)
			.sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));
	};

	const handleLogScore = async () => {
		if (!selectedWorkout) return;

		const availableCategories = getCategories(selectedWorkout);
		// Resolve the category to save: use the chosen one, or auto-pick when the
		// workout only has a single category (or none at all).
		const resolvedCategory =
			selectedCategory ||
			(availableCategories.length === 1 ? availableCategories[0].key : "");

		// Category is only required when the workout actually offers a choice.
		const needsCategory = availableCategories.length > 1;

		if ((needsCategory && !resolvedCategory) || !score.trim()) {
			setModal({
				show: true,
				title: "Missing Fields",
				message: needsCategory
					? "Please select a category and enter your score."
					: "Please enter your score.",
				type: "warning",
			});
			return;
		}
		try {
			await addWorkoutScore({
				workoutId: selectedWorkout.id,
				workoutTitle: selectedWorkout.title,
				category: resolvedCategory || "general",
				scoreType,
				score: score.trim(),
				comment: comment.trim(),
				completedAt: completedDate,
			});
			setScore("");
			setComment("");
			setSelectedCategory("");
			setScoreType("time");
			setCompletedDate(new Date().toISOString().split("T")[0]);
			setSelectedWorkout(null);
			setModal({
				show: true,
				title: "Score Logged",
				message: "Your score has been saved.",
				type: "success",
			});
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message,
				type: "error",
			});
		}
	};

	const handleDeleteScore = (scoreId) => {
		setConfirmModal({
			show: true,
			title: "Delete Score",
			message: "Are you sure you want to delete this score entry?",
			type: "delete",
			onConfirm: async () => {
				setConfirmModal((prev) => ({ ...prev, show: false }));
				try {
					await deleteWorkoutScore(scoreId);
				} catch (error) {
					setModal({
						show: true,
						title: "Error",
						message: error.message,
						type: "error",
					});
				}
			},
		});
	};

	return (
		<div>
			<div className="d-flex justify-content-between align-items-center mb-3">
				<h5 className="mb-0">
					<ClipboardList className="me-2" size={20} />
					Workouts
				</h5>
			</div>

			{/* Search & Date Filter */}
			<div className="mb-3">
				<div className="row g-2">
					<div className="col-12 col-sm">
						<div className="input-group">
							<span className="input-group-text">
								<Search size={16} />
							</span>
							<input
								type="text"
								className="form-control"
								placeholder="Search workouts by name..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
							/>
						</div>
					</div>
					<div className="col-12 col-sm-auto">
						<div className="input-group">
							<span className="input-group-text">
								<Calendar size={16} />
							</span>
							<select
								className="form-select"
								value={selectedDate}
								onChange={(e) => setSelectedDate(e.target.value)}
								style={{ minWidth: "180px" }}
							>
								<option value="">All dates</option>
								{workoutDates.map(([key, label]) => (
									<option key={key} value={key}>
										{label}
									</option>
								))}
							</select>
						</div>
					</div>
				</div>
			</div>

			{/* Score Entry Form */}
			{selectedWorkout && (
				<div
					ref={scoreFormRef}
					className="mb-4 p-3 bg-light rounded border"
					style={{ scrollMarginTop: "80px" }}
				>
					<h6>Log Score: {selectedWorkout.title}</h6>
					<div className="row g-3">
						{getCategories(selectedWorkout).length > 1 && (
							<div className="col-md-4">
								<label className="form-label">Category *</label>
								<select
									className="form-select"
									value={selectedCategory}
									onChange={(e) => setSelectedCategory(e.target.value)}
									disabled={loading}
								>
									<option value="">Select category</option>
									{getCategories(selectedWorkout).map((cat) => (
										<option key={cat.key} value={cat.key}>
											{cat.label}
										</option>
									))}
								</select>
							</div>
						)}
						<div className="col-md-3">
							<label className="form-label">Score Type</label>
							<select
								className="form-select"
								value={scoreType}
								onChange={(e) => setScoreType(e.target.value)}
								disabled={loading}
							>
								<option value="time">Time (mm:ss)</option>
								<option value="reps">Reps</option>
							</select>
						</div>
						<div className="col-md-3">
							<label className="form-label">Score *</label>
							<input
								type="text"
								className="form-control"
								value={score}
								onChange={(e) => setScore(e.target.value)}
								placeholder={scoreType === "time" ? "e.g. 12:30" : "e.g. 45"}
								disabled={loading}
							/>
						</div>
						<div className="col-md-2">
							<label className="form-label">Date</label>
							<input
								type="date"
								className="form-control"
								value={completedDate}
								onChange={(e) => setCompletedDate(e.target.value)}
								disabled={loading}
							/>
						</div>
						<div className="col-12">
							<label className="form-label">Comment (optional)</label>
							<textarea
								className="form-control"
								rows="2"
								value={comment}
								onChange={(e) => setComment(e.target.value)}
								placeholder="How did it go? Any notes on this workout..."
								disabled={loading}
							/>
						</div>
						<div className="col-12">
							<button
								className="btn btn-success me-2"
								onClick={handleLogScore}
								disabled={loading}
							>
								{loading ? "Saving..." : "Save Score"}
							</button>
							<button
								className="btn btn-secondary"
								onClick={() => setSelectedWorkout(null)}
								disabled={loading}
							>
								Cancel
							</button>
						</div>
					</div>

					{/* Show selected category content */}
					{selectedCategory && selectedWorkout[selectedCategory] && (
						<div
							className="mt-3 p-2 border rounded"
							style={{ backgroundColor: "#e1d0bb" }}
						>
							<small className="text-muted d-block mb-1">
								Workout details:
							</small>
							<p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
								{selectedWorkout[selectedCategory]}
							</p>
						</div>
					)}
				</div>
			)}

			{/* Workout List */}
			{filteredWorkouts.length === 0 ? (
				<div className="text-center py-5">
					<p className="text-muted">
						{searchTerm || selectedDate
							? "No workouts match your search."
							: "No workouts available yet."}
					</p>
				</div>
			) : (
				<div className="list-group">
					{filteredWorkouts.map((workout) => {
						const scores = getScoresForWorkout(workout.id);
						const isHistoryOpen = showHistory[workout.id];
						const categories = getCategories(workout);

						return (
							<div
								key={workout.id}
								className="list-group-item mb-2"
								style={{ borderRadius: "8px" }}
							>
								<div className="d-flex justify-content-between align-items-start">
									<div className="flex-grow-1">
										<h6 className="mb-1">{workout.title}</h6>
										<div className="d-flex gap-1 mb-2 flex-wrap">
											{categories.map((cat) => (
												<span
													key={cat.key}
													className={`badge ${cat.color}`}
													style={{ fontSize: "0.7rem" }}
												>
													{cat.label}
												</span>
											))}
										</div>
										<small className="text-muted">
											Published{" "}
											{new Date(workout.createdAt).toLocaleDateString()}
										</small>
									</div>
									<button
										className="btn btn-sm btn-primary"
										onClick={() => {
											const cats = getCategories(workout);
											setSelectedWorkout(workout);
											// Pre-select the only category so single-category workouts
											// can be logged without an extra step.
											setSelectedCategory(cats.length === 1 ? cats[0].key : "");
											setScore("");
											setComment("");
											setScoreType("time");
											setCompletedDate(new Date().toISOString().split("T")[0]);
										}}
										disabled={loading}
									>
										<ClipboardList size={16} className="d-inline d-sm-none" />
										<span className="d-none d-sm-inline">Log Score</span>
									</button>
								</div>

								{/* Category details (expandable) */}
								<div className="mt-2">
									{categories.map((cat) => (
										<div key={cat.key} className="mb-1">
											<span
												className={`badge ${cat.color} me-2`}
												style={{ fontSize: "0.7rem" }}
											>
												{cat.label}
											</span>
											<small style={{ whiteSpace: "pre-wrap" }}>
												{workout[cat.key]}
											</small>
										</div>
									))}
								</div>

								{/* Score History */}
								{scores.length > 0 && (
									<div className="mt-2">
										<button
											className="btn btn-sm btn-outline-secondary"
											onClick={() =>
												setShowHistory((prev) => ({
													...prev,
													[workout.id]: !prev[workout.id],
												}))
											}
										>
											<TrendingUp size={14} className="me-1" />
											{isHistoryOpen ? "Hide" : "View"} Progress (
											{scores.length})
											{isHistoryOpen ? (
												<ChevronUp size={14} className="ms-1" />
											) : (
												<ChevronDown size={14} className="ms-1" />
											)}
										</button>

										{isHistoryOpen && (
											<div className="mt-2">
												<div className="table-responsive">
													<table className="table table-sm table-hover mb-0">
														<thead>
															<tr>
																<th>Date</th>
																<th>Category</th>
																<th>Type</th>
																<th>Score</th>
																<th>Comment</th>
																<th></th>
															</tr>
														</thead>
														<tbody>
															{scores.map((s) => (
																<tr key={s.id}>
																	<td>
																		{new Date(
																			s.completedAt,
																		).toLocaleDateString()}
																	</td>
																	<td>
																		<span
																			className={`badge ${s.category === "foundation" ? "bg-success" : s.category === "build" ? "bg-warning text-dark" : "bg-danger"}`}
																		>
																			{s.category}
																		</span>
																	</td>
																	<td>
																		{s.scoreType === "time" ? (
																			<Clock size={14} className="me-1" />
																		) : (
																			<Hash size={14} className="me-1" />
																		)}
																		{s.scoreType === "time" ? "Time" : "Reps"}
																	</td>
																	<td>{s.score}</td>
																	<td>
																		{s.comment ? (
																			<small
																				className="text-muted"
																				style={{ fontStyle: "italic" }}
																			>
																				{s.comment}
																			</small>
																		) : (
																			<small className="text-muted">—</small>
																		)}
																	</td>
																	<td>
																		<button
																			className="btn btn-sm btn-outline-danger"
																			onClick={() => handleDeleteScore(s.id)}
																			disabled={loading}
																		>
																			<Trash2 size={12} />
																		</button>
																	</td>
																</tr>
															))}
														</tbody>
													</table>
												</div>
											</div>
										)}
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}

			<CustomModal
				show={modal.show}
				title={modal.title}
				message={modal.message}
				type={modal.type}
				onClose={() => setModal({ ...modal, show: false })}
			/>
			<CustomModal
				show={confirmModal.show}
				title={confirmModal.title}
				message={confirmModal.message}
				type={confirmModal.type}
				onConfirm={confirmModal.onConfirm}
				onClose={() => setConfirmModal({ ...confirmModal, show: false })}
				confirmText="Delete"
				loading={loading}
			/>
		</div>
	);
};

export default UserWorkouts;
